import { useTranslation } from "react-i18next";
import type { ContentLibraryItem } from "@/hooks/useContentLibrary";
import type { RecordingOpts } from "@/components/HomeFeed";
import type { CardContext } from "@/types/cardContext";
import {
  CARD_COLORS,
  bodyStyle,
  cardShellStyle,
  fullCtaStyle,
  labelStyle,
  titleStyle,
} from "./types";

interface MilestoneCardProps {
  milestone: ContentLibraryItem;
  onRecord: (opts?: RecordingOpts) => void;
}

const MilestoneCard = ({ milestone, onRecord }: MilestoneCardProps) => {
  const { t } = useTranslation();
  const colors = CARD_COLORS.milestone;
  const title = milestone.title ?? "A milestone";

  return (
    <div style={cardShellStyle(colors.bg, colors.border)}>
      {/* Top: label + title + body */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={labelStyle(colors.body)}>{t("cards.milestone.label")}</div>

        {milestone.subject === 'specific_child' && milestone._resolvedChildName && (
          <div style={{ ...labelStyle(colors.body), marginTop: 4 }}>
            {milestone._resolvedChildName}
          </div>
        )}

        <p
          style={{
            ...titleStyle(colors.title),
            marginTop: 6,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </p>

        <p
          style={{
            ...bodyStyle(colors.body),
            marginTop: 4,
            display: "-webkit-box",
            WebkitLineClamp: 6,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {milestone.body}
        </p>
      </div>

      {/* Bottom: CTA */}
      <div style={{ paddingTop: 12 }}>
        <button
          type="button"
          onClick={() => {
            const captureType = milestone.capture_type ?? "voice";
            const cardContext: CardContext = {
              contentLibraryId: milestone.id,
              title,
              body: milestone.body,
              instructions: milestone.instructions ?? undefined,
              tip: milestone.tip ?? undefined,
              captureType,
              tags: milestone.tags ?? [],
              cardType: milestone.card_type,
              voiceRequired: milestone.voice_required,
            };
            onRecord({
              contextPrompt: title,
              captureType: captureType as "voice" | "drawing" | "photo" | "written" | "video",
              cardContext,
              voiceRequired: milestone.voice_required,
            });
          }}
          style={fullCtaStyle(colors.title, colors.bg)}
        >
          Record this milestone 🎤
        </button>
      </div>
    </div>
  );
};

export default MilestoneCard;
